/**
 * useQuickRepliesPage - Hook para orquestrar a página de Respostas Rápidas
 * Separa lógica de estado e handlers do componente de apresentação.
 */
import { useState, useCallback, useMemo } from 'react';
import { useQuickReplies } from './useQuickReplies';
import { QuickReply } from '../../../../types';
import { notify } from '../../../../utils/toast';
import { TrashIcon } from '../../../../components/icons';
import { createImportAction, createExportAction } from '../../../../components/shared/ActionMenu';
import { BulkAction } from '../../../../components/shared/BulkActionsBar';

export const useQuickRepliesPage = () => {
    const {
        replies,
        loading,
        createReply,
        updateReply,
        deleteReply,
        deleteBulk,
        selectedIds,
        toggleSelection,
        toggleAllSelection,
        clearSelection,
        refetch
    } = useQuickReplies();

    const [searchTerm, setSearchTerm] = useState('');
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [replyToEdit, setReplyToEdit] = useState<QuickReply | undefined>(undefined);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [replyToDelete, setReplyToDelete] = useState<QuickReply | undefined>(undefined);
    const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);

    const filteredReplies = useMemo(() =>
        replies.filter(reply =>
            reply.shortcut.toLowerCase().includes(searchTerm.toLowerCase()) ||
            reply.message.toLowerCase().includes(searchTerm.toLowerCase())
        ), [replies, searchTerm]);

    const handleOpenCreateModal = useCallback(() => {
        setReplyToEdit(undefined);
        setIsFormModalOpen(true);
    }, []);

    const handleOpenEditModal = useCallback((reply: QuickReply) => {
        setReplyToEdit(reply);
        setIsFormModalOpen(true);
    }, []);

    const handleOpenDeleteModal = useCallback((reply: QuickReply) => {
        setReplyToDelete(reply);
        setIsDeleteModalOpen(true);
    }, []);

    const handleSaveReply = useCallback(async (data: Omit<QuickReply, 'id' | 'createdAt'>) => {
        try {
            if (replyToEdit) {
                await updateReply(replyToEdit.id, data);
                notify.success('Resposta rápida atualizada com sucesso!');
            } else {
                await createReply(data);
                notify.success('Resposta rápida criada com sucesso!');
            }
            setIsFormModalOpen(false);
        } catch (error) {
            notify.error('Erro ao salvar resposta rápida');
        }
    }, [replyToEdit, updateReply, createReply]);

    const handleConfirmDelete = useCallback(async () => {
        if (replyToDelete) {
            try {
                await deleteReply(replyToDelete.id);
                notify.success('Resposta rápida excluída com sucesso!');
                setIsDeleteModalOpen(false);
                setReplyToDelete(undefined);
            } catch (error) {
                notify.error('Erro ao excluir resposta rápida');
            }
        }
    }, [replyToDelete, deleteReply]);

    const handleBulkDelete = useCallback(async () => {
        const ids = Array.from(selectedIds);
        try {
            await deleteBulk(ids);
            notify.success(`${ids.length} resposta(s) excluída(s) com sucesso!`);
            setIsBulkDeleteModalOpen(false);
        } catch {
            notify.error('Erro ao excluir respostas');
        }
    }, [selectedIds, deleteBulk]);

    const handleDuplicate = useCallback(async (reply: QuickReply) => {
        try {
            const { id, ...data } = reply;
            await createReply({ ...data, shortcut: `${data.shortcut}-copy` });
            notify.success('Resposta rápida duplicada com sucesso!');
        } catch (error) {
            notify.error('Erro ao duplicar resposta rápida');
        }
    }, [createReply]);

    const handleRefresh = useCallback(async () => {
        if (refetch) {
            await refetch();
            notify.success('Dados atualizados!');
        }
    }, [refetch]);

    const headerActions = useMemo(() => [
        createImportAction(() => notify.info('Funcionalidade de importação em desenvolvimento')),
        createExportAction(() => notify.info('Funcionalidade de exportação em desenvolvimento')),
    ], []);

    const bulkActions: BulkAction[] = useMemo(() => [
        {
            label: 'Excluir',
            icon: TrashIcon,
            onClick: () => setIsBulkDeleteModalOpen(true),
            variant: 'danger' as const
        }
    ], []);

    return {
        // Data
        filteredReplies,
        loading,
        searchTerm,
        setSearchTerm,
        selectedIds,
        replyToEdit,
        replyToDelete,

        // Modal states
        isFormModalOpen,
        setIsFormModalOpen,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        isBulkDeleteModalOpen,
        setIsBulkDeleteModalOpen,

        // Handlers
        handleOpenCreateModal,
        handleOpenEditModal,
        handleOpenDeleteModal,
        handleSaveReply,
        handleConfirmDelete,
        handleBulkDelete,
        handleDuplicate,
        handleRefresh,

        // Selection
        toggleSelection,
        toggleAllSelection,
        clearSelection,

        // Actions config
        headerActions,
        bulkActions
    };
};
